import { Title } from '@solidjs/meta'
import { type Component, createMemo, createResource, For, Show } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Icon from '~/components/Icon'
import { useAuth } from '~/contexts/AuthContext'
import { useCompany } from '~/contexts/CompanyContext'
import { useCompanySettings } from '~/hooks/useCompanySettings'
import { type Path, useNavigate } from '~/router'
import { customerService } from '~/services/customer.service'
import { leadService } from '~/services/lead.service'
import { type QuoteType, quoteService } from '~/services/quote.service'
import { serviceCatalogService } from '~/services/serviceCatalog.service'
import { formatMoney } from '~/utils/settings'
import { quoteStatusLabels as statusLabels } from '~/utils/status'

const statusColors: Record<string, 'default' | 'success' | 'warning' | 'danger' | 'info'> = {
  draft: 'default',
  sent: 'info',
  approved: 'success',
  rejected: 'danger',
  expired: 'warning',
  cancelled: 'danger'
}

/** Punto de entrada del panel: el estado del negocio de un vistazo y los accesos del día a día. */
const DashboardPage: Component = () => {
  const auth = useAuth()
  const navigate = useNavigate()
  const { currentCompany } = useCompany()
  const { settings } = useCompanySettings()

  // Cada recurso falla por su cuenta: que no haya prospectos no debe tumbar el resto.
  const [quotes] = createResource(async () => {
    try {
      return (await quoteService.list({ limit: 100 })).items
    } catch {
      return [] as QuoteType[]
    }
  })

  const [customers] = createResource(async () => {
    try {
      return (await customerService.list({ limit: 1 })).meta.total
    } catch {
      return 0
    }
  })

  const [services] = createResource(async () => {
    try {
      return (await serviceCatalogService.list()).length
    } catch {
      return 0
    }
  })

  const [leads] = createResource(async () => {
    try {
      return (await leadService.list({ limit: 1 })).meta.total
    } catch {
      return 0
    }
  })

  const quoted = createMemo(() => {
    const items = quotes() ?? []
    const approved = items.filter(q => q.status === 'approved')
    const totalApproved = approved.reduce((acc, q) => acc + (Number(q.total) || 0), 0)
    return { count: items.length, pending: items.filter(q => q.status === 'sent').length, approved: approved.length, totalApproved }
  })

  const recent = createMemo(() => (quotes() ?? []).slice(0, 5))

  const money = (value: number, currency = 'COP') => formatMoney(value, currency, settings()?.currencies ?? [])

  const stats = createMemo(() => [
    {
      key: 'quotes',
      label: 'Cotizaciones',
      value: String(quoted().count),
      hint: `${quoted().pending} enviadas sin respuesta`,
      icon: 'material-symbols-light:description-outline',
      href: '/admin/cotizaciones' as Path
    },
    {
      key: 'approved',
      label: 'Aprobado',
      value: money(quoted().totalApproved),
      hint: `${quoted().approved} propuesta(s) ganada(s)`,
      icon: 'material-symbols-light:trending-up',
      href: '/admin/cotizaciones' as Path
    },
    {
      key: 'customers',
      label: 'Clientes',
      value: String(customers() ?? 0),
      hint: 'En tu cartera',
      icon: 'material-symbols-light:group-outline',
      href: '/admin/clientes' as Path
    },
    {
      key: 'services',
      label: 'Servicios',
      value: String(services() ?? 0),
      hint: `${leads() ?? 0} prospecto(s) del asesor`,
      icon: 'material-symbols-light:handyman-outline',
      href: '/admin/servicios' as Path
    }
  ])

  const shortcuts = [
    { label: 'Nueva cotización', icon: 'material-symbols-light:note-add-outline', href: '/admin/cotizaciones' as Path },
    { label: 'Nuevo cliente', icon: 'material-symbols-light:person-add-outline', href: '/admin/clientes' as Path },
    { label: 'Catálogo de servicios', icon: 'material-symbols-light:handyman-outline', href: '/admin/servicios' as Path },
    { label: 'Marca del PDF', icon: 'material-symbols-light:palette-outline', href: (currentCompany().id ? `/admin/empresas/${currentCompany().id}` : '/admin/empresas') as Path }
  ]

  return (
    <>
      <Title>Panel | Florez Jaramillo</Title>

      <div class="space-y-6">
        <div class="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 class="text-2xl font-bold text-foreground">Hola, {String(auth.user()?.name || 'Admin')}</h1>
            <p class="text-sm text-foreground-subtle">
              Estás en <strong>{currentCompany().name ?? 'tu empresa'}</strong>. Esto es lo que hay hoy.
            </p>
          </div>
          <Button onClick={() => navigate('/admin/cotizaciones')} prependIcon="material-symbols-light:add-circle-outline">
            Nueva Cotización
          </Button>
        </div>

        <div class="flex gap-4 overflow-x-auto hide-scrollbar mb-6 sm:grid sm:grid-cols-2 sm:overflow-visible xl:grid-cols-4">
          <For each={stats()}>
            {stat => (
              <Card class="min-w-40 sm:min-w-0 cursor-pointer transition-shadow hover:shadow-md" onClick={() => navigate(stat.href)}>
                <Card.Body>
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                      <p class="text-xs font-medium uppercase tracking-wide text-foreground-subtle">{stat.label}</p>
                      <p class="text-2xl font-bold text-foreground mt-1 truncate">{stat.value}</p>
                      <p class="text-xs text-foreground-subtle mt-1 truncate">{stat.hint}</p>
                    </div>
                    <div class="p-2 rounded-xl bg-primary-soft shrink-0">
                      <Icon icon={stat.icon} size={22} class="text-primary" />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}
          </For>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card class="lg:col-span-2">
            <Card.Header>
              <div class="flex items-center justify-between">
                <h2 class="text-lg font-semibold text-foreground">Últimas cotizaciones</h2>
                <Button variant="text" color="default" onClick={() => navigate('/admin/cotizaciones')}>
                  Ver todas
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <Show when={quotes.loading}>
                <div class="py-8 text-center text-sm text-foreground-subtle">Cargando...</div>
              </Show>
              <Show when={!quotes.loading && recent().length === 0}>
                <div class="py-8 text-center text-sm text-foreground-subtle">Aún no hay cotizaciones. Crea la primera.</div>
              </Show>
              <div class="divide-y divide-outline/30">
                <For each={recent()}>
                  {quote => (
                    <button
                      type="button"
                      class="w-full flex items-center justify-between gap-4 py-3 text-left hover:bg-surface-container transition-colors rounded-lg px-2"
                      onClick={() => navigate('/admin/cotizaciones')}
                    >
                      <div class="min-w-0">
                        <p class="text-sm font-medium text-foreground truncate">{quote.title}</p>
                        <p class="text-xs text-foreground-subtle truncate">
                          {quote.number} · {quote.customer?.name ?? 'Sin cliente'}
                        </p>
                      </div>
                      <div class="flex items-center gap-3 shrink-0">
                        <span class="text-sm font-semibold text-foreground">{money(Number(quote.total) || 0, quote.currency)}</span>
                        <Badge color={statusColors[quote.status]}>{statusLabels[quote.status]}</Badge>
                      </div>
                    </button>
                  )}
                </For>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <h2 class="text-lg font-semibold text-foreground">Accesos rápidos</h2>
            </Card.Header>
            <Card.Body>
              <div class="space-y-2">
                <For each={shortcuts}>
                  {shortcut => (
                    <Button variant="soft" color="default" class="w-full justify-start" prependIcon={shortcut.icon} onClick={() => navigate(shortcut.href)}>
                      {shortcut.label}
                    </Button>
                  )}
                </For>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  )
}

export default DashboardPage
